import { CartItem, CartModel } from "../../models/CartModel";
import { AppConstant } from "../../constant/AppConstant";
import { getLocalStorage, setLocalStorage, removeItemLocalStorage } from "../../helper/localStorageHelper";
import { showSuccessAlert } from "../../helper/alertHelper";
import { ProductDetailModel } from "../../models/ProductDetailModel";
import { PriceSelectionModel } from "../../models/PriceSelectionModel";
import { openConfirmDialog } from "../../components/CustomConfirmDialog";
import { PriceTypeEnum } from "../../constant/PriceTypeEnum";
import { formatPrice } from "../../helper/utility";
import { EVENTS, trackEvent } from "../../allTagsScriptsEvents/AnalyticsEvent";

export const getCart = (): CartModel => {
  const cart = getLocalStorage(AppConstant.CART_KEY);
  const parsedCart = cart ? JSON.parse(cart) : {};

  return {
    created_by_id: parsedCart.created_by_id ?? "",
    customer_id: parsedCart.customer_id ?? "",
    payment_id: parsedCart.payment_id ?? "",
    items: Array.isArray(parsedCart.items) ? parsedCart.items : [],
    order_status: 1,
    callout_price: formatPrice(parsedCart.callout_price ?? 0),
    sub_total: formatPrice(parsedCart.sub_total ?? 0),
    discount_amount: formatPrice(parsedCart.discount_amount ?? 0),
    total_gst_amount: formatPrice(parsedCart.total_gst_amount ?? 0),
    total_price: formatPrice(parsedCart.total_price ?? 0),
    plateform_charges: formatPrice(parsedCart.plateform_charges ?? 0),
    selected_price_total: formatPrice(parsedCart.selected_price_total ?? 0),
    customer_email: parsedCart.customer_email ?? "",
    billing_address: parsedCart.billing_address ?? "",
    fitting_date: parsedCart.fitting_date ?? "",
    fitting_time: parsedCart.fitting_time ?? "",
    is_paid: parsedCart.is_paid ?? false,
    type: parsedCart.type ?? 3,
    vehicle_name: parsedCart.vehicle_name ?? "",
    vehicle_model: parsedCart.vehicle_model ?? "",
    is_guest_order: parsedCart.is_guest_order ?? false,
    first_name: parsedCart.first_name ?? "",
    last_name: parsedCart.last_name ?? "",
    phone_number: parsedCart.phone_number ?? "",
    state_id: parsedCart.state_id ?? "",
    city_id: parsedCart.city_id ?? "",
    suburb_id: parsedCart.suburb_id ?? "",
    postal_code: parsedCart.postal_code ?? "",
  };
};

export const saveCart = (cart: CartModel): void => {
  setLocalStorage(AppConstant.CART_KEY, JSON.stringify(cart));
  if (window.updateCartCount) {
    window.updateCartCount();
  }
};

export const clearCart = (): void => {
  removeItemLocalStorage(AppConstant.CART_KEY);
  if (window.updateCartCount) {
    window.updateCartCount();
  }
};

export const calculateSubTotal = (items: CartItem[]) => {
  return items.reduce((total, item) => {
    // if (item.is_tax_included) {
    //   return total + ((item.total_price ?? 0) - (item.gst_amount ?? 0));
    // } else {
    //   return total + (item.total_price ?? 0);
    // }
    return total + (item.total_price ?? 0);
  }, 0);
};

export const calculateGSTAmount = (items: CartItem[]) => {
  return items.reduce((total, item) => total + (item.gst_amount || 0), 0);
};

export const calculateTotalDiscount = (items: CartItem[]) => {
  return items.reduce((total, item) => {
    const discountPerQty = item.discount_amount ?? 0;
    const promoMin = item.promo_min_qty ?? 4;

    if (item.quantity >= promoMin) {
      return total + (discountPerQty * item.quantity);
    }

    return total;
  }, 0);
};

export const calculateTotalPrice = (items: CartItem[], calloutPrice: number) => {
  // return calculateSubTotal(items) + calculateGSTAmount(items) + getCallOutPrice(items, calloutPrice);
  return calculateSubTotal(items) + calculateSelectedPriceTotal(items) + getCallOutPrice(items, calloutPrice);
};

export const calculatePlateformCharges = (totalPrice: number) => {
  return Number(((totalPrice * AppConstant.platformChargesPercentage) / 100).toFixed(2));
};

export const calculateSelectedPriceTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => {
    const perItemSelectedPrice = item.selected_price ?? 0;
    const qty = item.quantity ?? 0;

    return total + (perItemSelectedPrice * qty);
  }, 0);
};

export const updateExistingItem = (quantity: number, itemCode: string, isiIncreaseQty: boolean): CartModel => {
  const cart = getCart();
  if (!cart.items) {
    cart.items = [];
  }

  const existingItemIndex = cart.items.findIndex((item) => item.item_code === itemCode);

  if (existingItemIndex >= 0) {
    const existingItem = cart.items[existingItemIndex];
    if (existingItem) {
      const updatedQuantity = quantity;
      const sellingPrice = updatedQuantity < existingItem.promo_min_qty ? existingItem.original_price : existingItem.selling_price;

      // const totalPrice = formatPrice((sellingPrice * updatedQuantity) + (existingItem.selected_price * updatedQuantity));
      const totalPrice = formatPrice(sellingPrice * updatedQuantity);
      const gst_amount = formatPrice(totalPrice - (totalPrice / (1 + AppConstant.taxPercentageFromBackend / 100)));

      existingItem.price = sellingPrice;
      existingItem.product_id = existingItem.product_id;
      existingItem.quantity = updatedQuantity;
      existingItem.total_price = totalPrice;
      existingItem.gst_amount = gst_amount;
      existingItem.discount_amount = existingItem.discount_amount;
    }
  }

  const updatedItems = [...cart.items];
  const updatedCart = updateCartStateWithCalculations(cart, updatedItems);
  saveCart(updatedCart);
  if (isiIncreaseQty) {
    showSuccessAlert("Scoreboard Updated");
  } else {
    showSuccessAlert("That’s a wicket down — you’ve removed an item!");
  }
  return updatedCart;
};

export const updateExistingCart = (selectedCityPrice: {
  callout_price: number,
  mobile_fitting_price: number,
  store_price: number,
  delivery_price: number,
}) => {
  const cart = getCart();
  if (!cart.items) {
    cart.items = [];
  }

  const priceOptions: PriceSelectionModel[] = [
    {
      key: AppConstant.pickUpPrice,
      label: AppConstant.pickUpPrice,
      price: 0,
    },
    {
      key: PriceTypeEnum.Store,
      label: PriceTypeEnum.Store,
      price: selectedCityPrice.store_price ?? 0,
    },
    {
      key: PriceTypeEnum.MobileFitting,
      label: PriceTypeEnum.MobileFitting,
      price: selectedCityPrice.mobile_fitting_price ?? 0,
    },
  ];

  if (AppConstant.isDeliveryPriceShow) {
    priceOptions.push({
      key: PriceTypeEnum.Delivery,
      label: PriceTypeEnum.Delivery,
      price: selectedCityPrice.delivery_price ?? 0,
    });
  }

  let calloutPrice = 0;

  if (cart.items.length > 0) {
    const updatedItems = cart.items.map((item) => {
      const matchedPrice = priceOptions.find(
        (opt) => opt.key === item.selected_price_type
      );

      if (matchedPrice) {
        if (matchedPrice.key === PriceTypeEnum.MobileFitting) {
          calloutPrice = selectedCityPrice.callout_price;
        }

        return {
          ...item,
          selected_price: matchedPrice.price,
          is_selected_price: item.is_selected_price,
          selected_price_type: matchedPrice.key,
        };
      }

      return item;
    });

    cart.items = updatedItems;

    cart.callout_price = calloutPrice;

    const updatedCart = updateCartStateWithCalculations(cart, cart.items);
    saveCart(updatedCart);

    showSuccessAlert("Location change and price will be change");
  }
};

export const addToCart = (
  product: ProductDetailModel,
  quantity: number,
  selectedPriceObj: PriceSelectionModel | null,
  calloutPrice: number,
) => {
  const cart = getCart();

  const newType = selectedPriceObj ? selectedPriceObj.key : AppConstant.pickUpPrice;

  const conflictItem = cart.items?.find(
    (item) => item.selected_price_type !== newType
  );

  if (conflictItem) {
    openConfirmDialog(
      AppConstant.confirmDeleteOtherItem,
      AppConstant.confirmDeleteOtherItemMsgs,
      AppConstant.emptyCart,
      AppConstant.keepExisting,
      async () => {
        clearCart();
        addToCart(product, quantity, selectedPriceObj, calloutPrice);
      });
    return;
  }

  if (!cart.items) {
    cart.items = [];
  }

  let callout_price = 0;
  if (selectedPriceObj && selectedPriceObj.key === PriceTypeEnum.MobileFitting) {
    cart.callout_price = calloutPrice;
    callout_price = calloutPrice;
  } else {
    cart.callout_price = 0;
    callout_price = 0;
  }

  const sellingPrice = quantity < product.promo_min_qty ? product.original_price : product.selling_price;

  // const totalPrice = formatPrice((sellingPrice * quantity) + ((selectedPriceObj ? selectedPriceObj.price : 0) * quantity));
  const totalPrice = formatPrice(sellingPrice * quantity);
  const gst_amount = formatPrice((totalPrice - (totalPrice / (1 + AppConstant.taxPercentageFromBackend / 100))));

  cart.items.push({
    product_id: product._id,
    item_code: product.item_code,
    product_img: product.image_urls[0],
    product_title: product.title,
    quantity,
    selling_price: product.selling_price,
    original_price: product.original_price,
    price: sellingPrice,
    selected_price: selectedPriceObj ? selectedPriceObj.price : 0,
    total_price: totalPrice,
    gst_percentage: AppConstant.taxPercentageFromBackend,
    gst_amount: gst_amount,
    discount_amount: product.discount_amount,
    promo_min_qty: product.promo_min_qty ?? 0,
    tax_id: AppConstant.taxIdFromBackend,
    is_tax_included: true,
    is_selected_price: selectedPriceObj ? true : false,
    selected_price_type: selectedPriceObj ? selectedPriceObj.key : AppConstant.pickUpPrice,
  });

  const updatedItems = [...cart.items];
  const updatedCart = updateCartStateWithCalculations(cart, updatedItems);
  saveCart(updatedCart);

  trackEvent(EVENTS.ADD_TO_CART, {
    content_category: product.type,
    content_name: product.title,
    content_ids: [product._id],
    content_type: "product",
    value: totalPrice,
    currency: "USD",
    contents: [{
      item_code: product.item_code,
      gst_percentage: AppConstant.taxPercentageFromBackend,
      gst_amount: gst_amount,
      price: sellingPrice,
      quantity: quantity,
      total_price: totalPrice,
      callout_price: callout_price
    }]
  });

  if (newType === PriceTypeEnum.Store) {
    showSuccessAlert("four item secured! store fitting updated.");
  }
  else if (newType === PriceTypeEnum.MobileFitting) {
    showSuccessAlert("Sixer item secured! mobile fitting updated.");
  } else {
    showSuccessAlert("Sixer! Item Secured:");
  }

};

export const removeFromCart = (index: number): CartModel => {
  const cart = getCart();
  if (!cart.items) {
    cart.items = [];
  }
  const updatedItems = cart.items.filter((_, i) => i !== index);
  const updatedCart = updateCartStateWithCalculations(cart, updatedItems);
  saveCart(updatedCart);

  showSuccessAlert("Product OUT — sent back to the pavilion!");
  return updatedCart;
};

export const getTotalQty = (updatedItems: CartItem[]): number => {
  return updatedItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
};

export const getCallOutPrice = (updatedItems: CartItem[], callout_price: number): number => {
  const totalQty = getTotalQty(updatedItems);
  return totalQty < 3 ? callout_price : 0;
};

export const updateCartStateWithCalculations = (cart: CartModel, updatedItems: CartItem[]): CartModel => {
  const discountAmount = calculateTotalDiscount(updatedItems);
  const subTotal = calculateSubTotal(updatedItems); //- discountAmount;
  const gstAmount = calculateGSTAmount(updatedItems);
  const totalPrice = calculateTotalPrice(updatedItems, cart.callout_price);
  const selectedPriceTotal = calculateSelectedPriceTotal(updatedItems);
  const plateformCharges = calculatePlateformCharges(totalPrice);
  const finalPayAmout = totalPrice + plateformCharges;
  return {
    ...cart,
    items: updatedItems,
    sub_total: subTotal,
    total_gst_amount: gstAmount,
    discount_amount: discountAmount,
    total_price: finalPayAmout,
    selected_price_total: selectedPriceTotal,
    plateform_charges: plateformCharges,
  };
};